<script lang="ts">
    import AppLayout from '@/layouts/AppLayout.svelte';
    import { type BreadcrumbItem } from '@/types';
    import * as Card from '@/components/ui/card';
    import { Checkbox } from '@/components/ui/checkbox';
    import { Button } from '@/components/ui/button';
    import { Progress } from '@/components/ui/progress';
    import { RotateCcw, CheckCircle2, Circle } from 'lucide-svelte';

    const breadcrumbs: BreadcrumbItem[] = [
        { title: 'App Checklist', href: '/app-checklist' },
    ];

    const STORAGE_KEY = 'app-checklist-state';

    type ChecklistSection = {
        title: string;
        items: string[];
    };

    const checklistData: ChecklistSection[] = [
        {
            title: 'Authentication',
            items: [
                'Login with valid credentials',
                'Login with invalid credentials (should show error)',
                'Session persists after closing/reopening app',
                'Logout functionality works',
            ],
        },
        {
            title: 'Jobs List',
            items: [
                'Jobs load for the current date range',
                'Calendar navigation works (change dates)',
                'Jobs display correct info (customer, address, time)',
                'Tap a job to open job details',
                'Pull-to-refresh updates the list',
            ],
        },
        {
            title: 'Job Details',
            items: [
                'Job info displays correctly (customer, vehicle, address)',
                'Customer contact info is visible',
                'Photos section loads existing photos',
                'Can view full-size photos by tapping thumbnails',
                'Open site survey from job detail',
                'Submit site survey with photos',
                'View existing site survey',
                'Submit scaffolding handover with photos',
                'View existing scaffolding handover',
            ],
        },
        {
            title: 'Leads List',
            items: [
                'Leads load for the current date range',
                'Calendar navigation works (change dates)',
                'Lead display correct info (customer, address, time)',
                'Tap a lead to open lead details',
                'Pull-to-refresh updates the list',
            ],
        },
        {
            title: 'Lead Details',
            items: [
                'Lead info displays correctly (customer, stage, address)',
                'Customer contact info is visible',
                'Photos section loads existing photos',
                'Can view full-size photos by tapping thumbnails',
                'Submit site survey with photos',
                'View existing site survey',
            ],
        },
        {
            title: 'As Portal User',
            items: [
                'Can see all leads',
                'Can see all jobs',
                'Can see employees assigned to Jobs',
            ],
        },
        {
            title: 'As Employee',
            items: [
                'Can not see leads',
                'Can only see jobs assigned to them',
            ],
        },
        {
            title: 'Documents',
            items: [
                'Documents list loads',
                'Tap document to view PDF',
                'PDF renders correctly',
                'Can scroll through multi-page PDFs',
            ],
        },
        {
            title: 'Document Signing',
            items: [
                'Signature pad opens',
                'Can draw signature',
                'Can clear and redo signature',
                'Submit signed document uploads successfully',
            ],
        },
        {
            title: 'Offline Functionality (New Feature)',
            items: [
                'App handles no network gracefully',
                'Cached data displays when offline',
                'Offline indicator/banner shows when disconnected',
                'Download data works',
                'Ques form submission',
                'Data syncs when connection restored',
            ],
        },
        {
            title: 'General / UI',
            items: [
                'Navigation between screens works',
                'Back button behavior is correct',
                'Loading spinners show during API calls',
                'Error messages display clearly',
                'Debug log button (floating) is accessible',
                'Debug logs capture activity correctly',
            ],
        },
        {
            title: 'Edge Cases',
            items: [
                'Handle expired token (401) - redirects to login',
                'Handle server errors (500) gracefully',
                'Handle slow network connections',
                'App resumes correctly after backgrounding',
            ],
        },
    ];

    function loadState(): Record<string, boolean> {
        if (typeof window === 'undefined') return {};
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    }

    function saveState(state: Record<string, boolean>) {
        if (typeof window === 'undefined') return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }

    function getKey(sectionIdx: number, itemIdx: number): string {
        return `${sectionIdx}-${itemIdx}`;
    }

    let checkedState = $state<Record<string, boolean>>(loadState());

    function toggleItem(sectionIdx: number, itemIdx: number) {
        const key = getKey(sectionIdx, itemIdx);
        checkedState[key] = !checkedState[key];
        saveState(checkedState);
    }

    function isChecked(sectionIdx: number, itemIdx: number): boolean {
        return checkedState[getKey(sectionIdx, itemIdx)] ?? false;
    }

    function resetAll() {
        checkedState = {};
        saveState(checkedState);
    }

    function getSectionProgress(sectionIdx: number): { completed: number; total: number } {
        const section = checklistData[sectionIdx];
        const total = section.items.length;
        let completed = 0;
        for (let i = 0; i < total; i++) {
            if (isChecked(sectionIdx, i)) completed++;
        }
        return { completed, total };
    }

    let totalItems = $derived(checklistData.reduce((sum, s) => sum + s.items.length, 0));
    let completedItems = $derived(Object.values(checkedState).filter(Boolean).length);
    let overallProgress = $derived(totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0);
</script>

<svelte:head>
    <title>App Checklist</title>
</svelte:head>

<AppLayout {breadcrumbs}>
    <div class="space-y-6 p-6">
        <!-- Header with Progress -->
        <Card.Root>
            <Card.Header class="pb-3">
                <div class="flex items-center justify-between">
                    <div>
                        <Card.Title class="text-xl">Mobile App Testing Checklist</Card.Title>
                        <Card.Description>
                            Track your testing progress. State persists in browser storage.
                        </Card.Description>
                    </div>
                    <Button variant="outline" size="sm" onclick={resetAll}>
                        <RotateCcw class="mr-2 h-4 w-4" />
                        Reset All
                    </Button>
                </div>
            </Card.Header>
            <Card.Content>
                <div class="space-y-2">
                    <div class="flex justify-between text-sm">
                        <span class="text-muted-foreground">Overall Progress</span>
                        <span class="font-medium">{completedItems} / {totalItems} ({overallProgress}%)</span>
                    </div>
                    <Progress value={overallProgress} class="h-2" />
                </div>
            </Card.Content>
        </Card.Root>

        <!-- Checklist Sections -->
        <div class="grid gap-4 md:grid-cols-2">
            {#each checklistData as section, sectionIdx}
                {@const progress = getSectionProgress(sectionIdx)}
                {@const sectionComplete = progress.completed === progress.total}
                <Card.Root class={sectionComplete ? 'border-green-500/50 bg-green-500/5' : ''}>
                    <Card.Header class="pb-3">
                        <div class="flex items-center justify-between">
                            <Card.Title class="flex items-center gap-2 text-base">
                                {#if sectionComplete}
                                    <CheckCircle2 class="h-5 w-5 text-green-500" />
                                {:else}
                                    <Circle class="h-5 w-5 text-muted-foreground" />
                                {/if}
                                {section.title}
                            </Card.Title>
                            <span class="text-sm text-muted-foreground">
                                {progress.completed}/{progress.total}
                            </span>
                        </div>
                    </Card.Header>
                    <Card.Content class="space-y-3">
                        {#each section.items as item, itemIdx}
                            {@const checked = isChecked(sectionIdx, itemIdx)}
                            <label
                                class="flex cursor-pointer items-start gap-3 rounded-md p-2 transition-colors hover:bg-muted/50"
                            >
                                <Checkbox
                                    {checked}
                                    onCheckedChange={() => toggleItem(sectionIdx, itemIdx)}
                                    class="mt-0.5"
                                />
                                <span
                                    class={[
                                        'text-sm leading-relaxed',
                                        checked ? 'text-muted-foreground line-through' : '',
                                    ].join(' ')}
                                >
                                    {item}
                                </span>
                            </label>
                        {/each}
                    </Card.Content>
                </Card.Root>
            {/each}
        </div>
    </div>
</AppLayout>
